import React from 'react';
import { 
  Eye, Keyboard, Brain, Code,
  Check, FileText, Monitor, Users,
  Mail, Accessibility
} from 'lucide-react';
import Brand from '../components/Brand';

const WCAGChecklist = () => {
  return (
    <div className="w-[8.5in] max-w-full h-[11in] mx-auto bg-white print:m-0 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24" />
        
        <div className="relative z-10">
          <Brand theme="dark" markClassName="text-white" className="text-3xl font-bold mb-4" />
          
          <h1 className="text-3xl font-bold mb-3">WCAG 2.1 Checklist for Web Accessibility</h1>
          <p className="text-lg text-purple-50 max-w-4xl">
            Ensure your digital services are accessible to all residents with this comprehensive compliance checklist.
          </p>
        </div>
        
        {/* Accessibility Badge */}
        <div className="absolute top-6 right-6 bg-white text-purple-600 p-3 rounded-full shadow-lg">
          <Accessibility className="h-8 w-8" />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
          {/* 1. Perceivable */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Eye className="h-6 w-6 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">1. Perceivable</h2>
            </div>
            
            <div className="space-y-3 ml-8 text-sm">
              <div className="flex items-start gap-3">
                <div className="h-5 w-5 border-2 border-gray-400 rounded flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">1.1.1 Non-text Content:</div>
                  <p className="text-gray-600 text-sm mt-1">Provide text alternatives for non-text content.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="h-5 w-5 border-2 border-gray-400 rounded flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">1.2.1 Audio/Video-only:</div>
                  <p className="text-gray-600 text-sm mt-1">Provide alternatives for time-based media.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="h-5 w-5 border-2 border-gray-400 rounded flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">1.3.1 Info and Relationships:</div>
                  <p className="text-gray-600 text-sm mt-1">Ensure structure is available in markup.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="h-5 w-5 border-2 border-gray-400 rounded flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">1.4.3 Contrast (Minimum):</div>
                  <p className="text-gray-600 text-sm mt-1">Text contrast ratio of at least 4.5:1.</p>
                </div>
              </div>
            </div>
          </div>

          {/* 2. Operable */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Keyboard className="h-6 w-6 text-green-600" />
              <h2 className="text-xl font-bold text-gray-900">2. Operable</h2>
            </div>
            
            <div className="space-y-2 ml-8 text-sm">
              <div className="flex items-start gap-2">
                <div className="h-5 w-5 border-2 border-gray-400 rounded flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <span className="font-semibold text-gray-900">2.1.1 Keyboard:</span>
                  <p className="text-gray-700 text-xs">All functionality available from keyboard.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-2">
                <div className="h-5 w-5 border-2 border-gray-400 rounded flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <span className="font-semibold text-gray-900">2.2.1 Timing Adjustable:</span>
                  <p className="text-gray-700 text-xs">Provide mechanisms to adjust time limits.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-2">
                <div className="h-5 w-5 border-2 border-gray-400 rounded flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <span className="font-semibold text-gray-900">2.3.1 Three Flashes:</span>
                  <p className="text-gray-700 text-xs">Avoid content flashing more than 3 times/sec.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-2">
                <div className="h-5 w-5 border-2 border-gray-400 rounded flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <span className="font-semibold text-gray-900">2.4.1 Bypass Blocks:</span>
                  <p className="text-gray-700 text-xs">Provide a way to bypass blocks of content.</p>
                </div>
              </div>
            </div>
          </div>

          {/* 3. Understandable */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Brain className="h-6 w-6 text-orange-600" />
              <h2 className="text-xl font-bold text-gray-900">3. Understandable</h2>
            </div>
            
            <div className="space-y-2 ml-8 text-sm">
              <div className="flex items-start gap-2">
                <div className="h-5 w-5 border-2 border-gray-400 rounded flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <span className="font-semibold text-gray-900">3.1.1 Language of Page:</span>
                  <p className="text-gray-700 text-xs">Identify the language of page in markup.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-2">
                <div className="h-5 w-5 border-2 border-gray-400 rounded flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <span className="font-semibold text-gray-900">3.2.1 On Focus:</span>
                  <p className="text-gray-700 text-xs">Avoid context changes when focused.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-2">
                <div className="h-5 w-5 border-2 border-gray-400 rounded flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <span className="font-semibold text-gray-900">3.3.1 Error Identification:</span>
                  <p className="text-gray-700 text-xs">Provide descriptive error messages.</p>
                </div>
              </div>
            </div>
          </div>

          {/* 4. Robust */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Code className="h-6 w-6 text-purple-600" />
              <h2 className="text-xl font-bold text-gray-900">4. Robust</h2>
            </div>
            
            <div className="space-y-2 ml-8 text-sm">
              <div className="flex items-start gap-2">
                <div className="h-5 w-5 border-2 border-gray-400 rounded flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <span className="font-semibold text-gray-900">4.1.1 Parsing:</span>
                  <p className="text-gray-700 text-xs">Ensure markup is properly structured.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-2">
                <div className="h-5 w-5 border-2 border-gray-400 rounded flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <span className="font-semibold text-gray-900">4.1.2 Name, Role, Value:</span>
                  <p className="text-gray-700 text-xs">UI components have determinable names/roles.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Note Section */}
        <div className="bg-purple-50 rounded-2xl p-6 my-6 border border-purple-200">
          <div className="flex items-start gap-3">
            <FileText className="h-6 w-6 text-purple-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-gray-900 mb-2">How to Use This Checklist</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                This checklist covers the most critical WCAG 2.1 Level AA success criteria for public housing websites. 
                Use it to evaluate your digital services and ensure they meet accessibility standards. 
                Check each box as you verify compliance, and contact our accessibility team for guidance on implementation.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="text-center mt-auto">
          <div className="flex items-center justify-center gap-4 mb-3">
            <h3 className="text-xl font-bold text-gray-900">Contact Us:</h3>
            <div className="flex flex-wrap items-center gap-2">
              <Mail className="h-6 w-6 text-purple-600" />
              <span className="text-xl font-semibold text-purple-600">accessibility@gethousingai.com</span>
            </div>
          </div>
          <p className="text-lg text-gray-700 italic">Let's advance fair housing together!</p>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-white p-4 text-center mt-auto">
        <Brand theme="dark" markClassName="text-blue-600" className="justify-center text-xl font-bold mb-2" />
        <p className="text-gray-300">© 2026 HousingAI. All rights reserved.</p>
      </div>
    </div>
  );
};

export default WCAGChecklist;